import { AxiosInstance } from "axios";
import { isNil, toNumber } from "lodash";
import moment from "moment";
import { SelectProps as AntdSelectProps } from "antd";
import { BusAdminRole } from "../constants/constants";
import {
  LOCAL_STORAGE_TOKEN_KEY,
  LOCAL_STORAGE_USER_KEY,
} from "../constants/keys";
import { Login, RolePermission } from "../redux/Slices/BusAdmin/BusAdmin.util";
import { Trip } from "../redux/Slices/Trip/Trip.util";
import { ApiState } from "../redux/redux.type";
import { toET } from "../component/EthiopianDatePicker/EthiopianDatePicker.util";
import ExcelJs from "exceljs";
import { saveAs } from "file-saver";
import { ConductorDailyAudit } from "../redux/Slices/FinanceAudit/FinanceAudit.util";

export const logout = (): void => {
  sessionStorage.setItem(LOCAL_STORAGE_USER_KEY, "");
  sessionStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, "");
  sessionStorage.setItem("expiresIn", "");
};

export const getEtMonthDuration = (etDate: string) => {
  const [year, month, date] = etDate.split("-");
  if (month === "13")
    return { start: `${year}-${month}-1`, end: `${year}-${month}-5` };
  return { start: `${year}-${month}-1`, end: `${year}-${month}-30` };
};

export const isLoggedIn = () => {
  const token = sessionStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
  const expiresIn = sessionStorage.getItem("expiresIn");

  if (!token || isNil(token) || token === "") {
    logout();
    return false;
  }

  if (expiresIn) {
    const expiresInDate = moment.unix(Number(expiresIn));

    if (!expiresInDate.isValid()) {
      logout();
      return false;
    }

    const duration = expiresInDate.diff(moment(), "hour", true);

    if (duration < 0) {
      logout();
      return false;
    }

    return true;
  }

  logout();
  return false;
};

export const initAxios = (axiosInstance: AxiosInstance, token: any) => {
  axiosInstance.defaults.headers.common = {
    Authorization: `Bearer ${
      token ? token : sessionStorage.getItem(LOCAL_STORAGE_TOKEN_KEY)
    }`,
  };
};

export const saveUserData = (payload: Login) => {
  sessionStorage.setItem(
    LOCAL_STORAGE_USER_KEY,
    JSON.stringify({
      role: payload.role,
      id: payload.id,
      permissions: payload.permissions || [],
    })
  );
  sessionStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, payload.access_token);
  sessionStorage.setItem(
    "expiresIn",
    moment().add(12, "hours").unix().toString()
  );
};

export const getUserData = (): {
  role: string;
  id: number;
  permissions?: RolePermission[];
} | null => {
  const user = sessionStorage.getItem(LOCAL_STORAGE_USER_KEY);
  if (user) {
    return JSON.parse(user);
  }
  return null;
};

export const generateRouteString = (value: string) =>
  value.toLowerCase().split(" ").join("-");

export const formatNumber = (x: string | number | null | undefined) => {
  if (isNil(x)) {
    return "0";
  } else {
    var val = Math.round(Number(x!) * 100) / 100;
    var parts = val.toString().split(".");
    var num =
      parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",") +
      (parts[1] ? "." + parts[1] : "");
    return num;
  }
};

export const InputNumberProps = () => {
  const style = { width: "100%" };

  const formatter = (value: any) =>
    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const parser = (value: any) =>
    toNumber(value ? value.replace(/\$\s?|(,*)/g, "") : "");

  return { style, formatter, parser };
};

export const SelectProps = () => {
  const style = { width: "100%" };
  const showSearch = true;
  const optionFilterProp = "children";
  const filterOption = (input: any, option: any) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  return { style, showSearch, optionFilterProp, filterOption };
};

export const onItemChange = (
  dataAction: [any[], React.Dispatch<React.SetStateAction<any[]>>],
  key: any,
  selector: string,
  value: any
) => {
  const [data, setData] = dataAction;

  const index = data.findIndex((e) => e.key === key);

  if (index !== -1) {
    let temp = [...data];
    temp[index] = { ...temp[index], [selector]: value, updated: true };

    setData(temp);
  }
};

export const onItemArraySelection = (
  dataAction: [any[], React.Dispatch<React.SetStateAction<any[]>>],
  key: any,
  selector: string,
  value: any
) => {
  const [data, setData] = dataAction;

  const index = data.findIndex((e) => e.key === key);

  if (index !== -1) {
    let temp = [...data];
    let member = { ...temp[index] };

    if (member[selector]) {
      temp[index] = {
        ...temp[index],
        [selector]: [...member[selector], value],
        updated: true,
      };
    } else {
      temp[index] = { ...temp[index], [selector]: [value], updated: true };
    }

    setData(temp);
  }
};

export const onItemArrayDeselection = (
  dataAction: [any[], React.Dispatch<React.SetStateAction<any[]>>],
  key: any,
  selector: string,
  value: any
) => {
  const [data, setData] = dataAction;

  const index = data.findIndex((e) => e.key === key);

  if (index !== -1) {
    let temp = [...data];
    let member = { ...temp[index] };

    if (member[selector]) {
      temp[index] = {
        ...temp[index],
        [selector]: member[selector].filter((e: any) => e !== value),
        updated: true,
      };
    } else {
      temp[index] = { ...temp[index], [selector]: [value], updated: true };
    }

    setData(temp);
  }
};

export const onItemRemove = (
  dataAction: [any[], React.Dispatch<React.SetStateAction<any[]>>],
  key: any
) => {
  const [data, setData] = dataAction;

  const index = data.findIndex((e) => e.key === key);

  if (index !== -1) {
    if (!isNil(data[index].id)) {
      let temp = [...data];
      temp[index].deleted = true;
      setData(temp);
    } else {
      setData(data.filter((e) => e.key !== key));
    }
  }
};

export const zeroPad = (num: any, len: number = 4): string =>
  String(num).padStart(len, "0");

export const generateRandomColor = () => {
  let maxVal = 0xffffff; // 16777215
  let randomNumber: any = Math.random() * maxVal;
  randomNumber = Math.floor(randomNumber);
  randomNumber = randomNumber.toString(16);
  let randColor = randomNumber.padStart(6, 0);
  return `#${randColor.toUpperCase()}`;
};

export const checkAuthorization = (
  allowed_roles: string[],
  login?: ApiState<Login | null>
) => {
  const user = getUserData();
  const role = user?.role ? user?.role : login?.payload?.role;

  if (role) {
    if (
      role === BusAdminRole.Basic ||
      role === BusAdminRole.DirectorAuditor ||
      role === BusAdminRole.Director
    )
      return true;
    else return allowed_roles.includes(role);
  } else {
    return false;
  }
};

export const checkStrictAuthorization = (
  allowed_roles: string[],
  login?: ApiState<Login | null>
) => {
  const user = getUserData();
  const role = user?.role ? user?.role : login?.payload?.role;

  if (role) {
    return allowed_roles.includes(role);
  } else {
    return false;
  }
};

export const TripUniqueIdentifier = (trip: Trip) => {
  const id = `T${zeroPad(trip.id)}`;
  const date = toET(moment(trip.travelDate))
    .split("-")
    .map((e) => zeroPad(+e, 2))
    .join("");
  const route = trip?.route ? `R${zeroPad(trip.route.id)}` : "";
  const sideNo = trip.bus ? `SN${zeroPad(trip.bus.id)}` : "";
  const conductor = trip?.conductor ? `C${zeroPad(trip.conductor.id)}` : "";

  const identifier = [id, date, route, sideNo, conductor]
    .filter((e) => e)
    .join("/");

  return identifier;
};
export function numberToWordsEnglish(num: number): string {
  const ones = [
    "",
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
  ];
  const teens = [
    "",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
  ];
  const tens = [
    "",
    "ten",
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "eighty",
    "ninety",
  ];

  if (num === 0) {
    return "zero";
  } else if (num < 10) {
    return ones[num];
  } else if (num < 20) {
    return teens[num - 10];
  } else if (num < 100) {
    return (
      tens[Math.floor(num / 10)] + (num % 10 !== 0 ? " " + ones[num % 10] : "")
    );
  } else if (num < 1000) {
    return (
      ones[Math.floor(num / 100)] +
      " hundred" +
      (num % 100 !== 0 ? " and " + numberToWordsEnglish(num % 100) : "")
    );
  } else if (num < 100000) {
    return (
      numberToWordsEnglish(Math.floor(num / 1000)) +
      " thousand" +
      (num % 1000 !== 0 ? " " + numberToWordsEnglish(num % 1000) : "")
    );
  } else {
    return "Number out of range";
  }
}

export function amountToWordsEnglish(amount: number): string {
  let birr = Math.floor(amount);
  let cents = Math.round((amount - birr) * 100);

  if (cents === 100) {
    birr += 1;
    cents = 0;
  }

  const birrWords = numberToWordsEnglish(birr);
  let result = `${birrWords} birr`;

  if (cents > 0) {
    const centsWords = numberToWordsEnglish(cents);
    const centLabel = cents === 1 ? "cent" : "cents";
    result += ` and ${centsWords} ${centLabel}`;
  }

  result += " only";
  return result;
}

function toNumeralsAM(n: number): any {
  if (n < 0 || n > 999_999_999) {
    throw new Error("Only integers 0–999 999 999 are supported");
  }

  const ones = [
    "ዜሮ",
    "አንድ",
    "ሁለት",
    "ሶስት",
    "አራት",
    "አምስት",
    "ስድስት",
    "ሰባት",
    "ስምንት",
    "ዘጠኝ",
    "አሥር",
    "አስራ አንድ",
    "አስራ ሁለት",
    "አስራ ሶስት",
    "አስራ አራት",
    "አስራ አምስት",
    "አስራ ስድስት",
    "አስራ ሰባት",
    "አስራ ስምንት",
    "አስራ ዘጠኝ",
  ];
  const tens = ["", "", "ሀያ", "ሰላሳ", "አርባ", "ሀምሳ", "ስልሳ", "ሰባ", "ሰማንያ", "ዘጠና"];
  const scales = [
    { value: 1_000_000, name: "ሚሊዮን" },
    { value: 1_000, name: "ሺህ" },
    { value: 100, name: "መቶ" },
  ];

  if (n < 20) {
    return ones[n];
  }
  if (n < 100) {
    const t = Math.floor(n / 10),
      u = n % 10;
    return tens[t] + (u ? " " + ones[u] : "");
  }

  for (let { value, name } of scales) {
    if (n >= value) {
      const major = Math.floor(n / value),
        rest = n % value;
      const majorWords =
        value === 100
          ? ones[major] + " " + name
          : toNumeralsAM(major) + " " + name;
      return rest ? majorWords + " " + toNumeralsAM(rest) : majorWords;
    }
  }

  return "";
}

export function numberToWordsAmharic(num: number) {
  if (num < 0 || num >= 1_000_000_000) {
    throw new Error("Only 0.00–999,999,999.99 supported");
  }
  const [wholeStr, decStr = "00"] = num.toFixed(2).split(".");
  const whole = parseInt(wholeStr, 10),
    cents = parseInt(decStr, 10);

  let result = toNumeralsAM(whole) + " ብር";
  if (cents > 0) {
    result += " ከ " + toNumeralsAM(cents) + " ሳንቲም";
  }
  return result;
}

export const exportConductorDailyAuditExcel = (
  date: string,
  data: ConductorDailyAudit[]
) => {
  const workbook = new ExcelJs.Workbook();
  const sheet = workbook.addWorksheet("E-TICKETING PAID REPORT");

  // Add header
  sheet.mergeCells("A1:I1");
  sheet.getCell("A1").value = "PUBLIC SERVICE TRANSPORT SERVICE";
  sheet.getCell("A1").alignment = { horizontal: "center" };
  sheet.getCell("A1").font = { bold: true, size: 14 };

  sheet.mergeCells("A2:I2");
  sheet.getCell("A2").value = "የፐብሊክ ሰርቪስ ትራንስፖርት አገልግሎት";
  sheet.getCell("A2").alignment = { horizontal: "center" };
  sheet.getCell("A2").font = { bold: true, size: 12 };

  sheet.mergeCells("A3:I3");
  sheet.getCell("A3").value = "E-TICKETING PAID REPORT (AGENT & CONDUCTOR)";
  sheet.getCell("A3").alignment = { horizontal: "center" };
  sheet.getCell("A3").font = { bold: true, size: 12 };

  sheet.mergeCells("A5:I5");
  sheet.getCell("A5").value = `Date Range: ${date} E.C`;
  sheet.getCell("A5").alignment = { horizontal: "center" };
  sheet.getCell("A5").font = { italic: true, size: 10 };

  // Add table headers manually
  const headers = [
    "S.N",
    "Conductor Name",
    "Driver Name",
    "Bus Side No",
    "Trip Date",
    "Route",
    "Paid Amount",
    "Conductor CSI",
    "Status",
  ];

  sheet.addRow(headers);

  // Set column widths
  sheet.columns = [
    { width: 5 }, // S.N
    { width: 20 }, // Conductor Name
    { width: 20 }, // Driver Name
    { width: 15 }, // Bus Side No
    { width: 15 }, // Trip Date
    { width: 25 }, // Route
    { width: 15 }, // Paid Amount
    { width: 15 }, // Conductor CSI
    { width: 10 }, // Status
  ];

  // Add table data
  data.forEach((item, index) => {
    const conductorName = item.conductor;
    const busSideNo = item.sideNumber;
    const route = `${item.from} - ${item.to}`;
    const conductorCsi = item.conductorCsi?.join(", ") || "-";
    const agentCsi = item.agentCsi?.join(", ") || "-";
    const status = item.remaining > 0 ? "Unpaid" : "Paid";

    sheet.addRow([
      index + 1, // S.N
      conductorName, // Conductor Name
      "", // Driver Name (not available)
      busSideNo, // Bus Side No
      item.date, // Trip Date
      route, // Route
      item.paid, // Paid Amount
      conductorCsi, // Conductor CSI
      status, // Status
    ]);
  });

  // Add empty rows for template (minimum 5 rows)
  const totalRows = Math.max(5, data.length);
  for (let i = data.length; i < totalRows; i++) {
    sheet.addRow([
      i + 1, // S.N
      "", // Conductor Name
      "", // Driver Name
      "", // Bus Side No
      "", // Trip Date
      "", // Route
      "", // Paid Amount
      "", // Conductor CSI
      "", // Status
    ]);
  }

  // Apply styling to all cells
  sheet.eachRow((row, rowNumber) => {
    row.eachCell((cell, colNumber) => {
      if (rowNumber >= 7) {
        // Apply border to all rows from headers onwards
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      }
      if (rowNumber === 7) {
        // Header row - make it bold and centered
        cell.font = { bold: true };
        cell.alignment = { horizontal: "center" };
      }
    });
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const fileName = `E-TICKETING_PAID_REPORT_${date.replace(/\//g, "-")}.xlsx`;
    saveAs(blob, fileName);
  });
};
